import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import "./BagsPurses.css";
import { supabase } from "../lib/supabase";
import { getSupabaseProductsByCategory, getSupabaseProductsByCategoryId } from "../lib/productService";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import Pagination from "../components/Pagination";

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);

const PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams();
  const { addToCart, incQty, decQty, getQty, toggleWish, isWished } = useCartWishlist();

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const { data: cat, error: catErr } = await supabase
          .from("categories")
          .select("*")
          .eq("slug", slug)
          .maybeSingle();

        if (catErr) throw catErr;
        if (!cat) {
          setCategory(null);
          setProducts([]);
          setLoading(false);
          return;
        }

        setCategory(cat);
        const list = await getSupabaseProductsByCategoryId(cat.id);
        const fallbackList = list.length === 0 ? await getSupabaseProductsByCategory(cat.name) : list;

        setProducts(
          (fallbackList || []).map((p) => ({
            id: p.id,
            name: p.title,
            price: p.offerPrice,
            img: p.images?.[0],
            link: `/product/${p.slug || p.id}`,
            isBestseller: p.isBestseller,
          }))
        );
      } catch (err) {
        console.error("CategoryPage load error:", err);
        setError(err?.message || "Unable to load this category.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [slug]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return products.slice(start, start + PER_PAGE);
  }, [products, currentPage]);

  const totalPages = Math.ceil(products.length / PER_PAGE) || 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [products.length]);



  const toggleWishlist = (item) => {
    toggleWish({ ...item, link: item.link || `/${item.id}` });
  };

  const renderCard = (item) => {
    const qty = getQty(item.id);
    const wished = isWished(item.id);
    return (
      <Link key={item.id} to={item.link} className="bCardLink">
        <article className="bCard" tabIndex={0}>
          <div className="bMedia">
            {item.isBestseller && <div className="bRibbon">Bestseller</div>}
            <img
              className="bImg"
              src={
                item.img ||
                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f3f3' width='200' height='200'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E"
              }
              alt={item.name}
            />
          </div>
          <div className="bInfo">
            <h3 className="bName">{item.name}</h3>
            <div className="bMeta">
              <div className="bPrice">₹ {formatINR(item.price)}</div>
              <button
                className={`bWish ${wished ? "active" : ""}`}
                type="button"
                aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWishlist(item);
                }}
              >
                {wished ? "♥" : "♡"}
              </button>
            </div>
            {qty === 0 ? (
              <button
                className="bAddCart"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToCart(item);
                }}
              >
                Add to Cart
              </button>
            ) : (
              <div
                className="bQtyWrap"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <button
                  className="bQtyBtn"
                  type="button"
                  aria-label="Decrease"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    decQty(item.id);
                  }}
                >
                  −
                </button>
                <span className="bQtyNum">{qty}</span>
                <button
                  className="bQtyBtn"
                  type="button"
                  aria-label="Increase"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    incQty(item.id);
                  }}
                >
                  +
                </button>
              </div>
            )}
          </div>
        </article>
      </Link>
    );
  };

  if (!loading && !category && !error) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bagsPage">
      <section className="bSection">
        <div className="bWrap">
          <h2 className="bTitle">{category?.name || "Category"}</h2>
          {error && (
            <div className="bComingSoon">
              <p className="bComingSoonNote">{error}</p>
            </div>
          )}
          {loading ? (
            <div className="bComingSoon">
              <p className="bComingSoonNote">Loading...</p>
            </div>
          ) : products.length > 0 ? (
            <div className="bSubSection">
              <div className="bSubHead">
                <h3 className="bSubTitle">Our Collection</h3>
                <div className="bSubLine" />
              </div>

              <div className="bGrid">
                {paginatedProducts.map((item) => renderCard(item))}
              </div>

              {totalPages > 1 && (
                <div className="bPagination">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="bComingSoon">
              <p className="bComingSoonNote">
                Products will be added from the Dashboard. Check back later!
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

